// Indicador de atividade do contato.
//
// ATENÇÃO — isto NÃO é o status "online" do WhatsApp, e o nome das funções evita
// de propósito a palavra "online" pra ninguém confundir depois.
//
// O que medimos: há quanto tempo o CONTATO nos mandou a última mensagem.
// O que NÃO medimos: se ele está com o WhatsApp aberto agora.
//
// Por que não dá pra medir presença de verdade (investigado em 2026-09-23):
// o protocolo exige presenceSubscribe por contato, e a Evolution 2.3.7 não expõe
// isso isoladamente — o único endpoint (/chat/sendPresence) assina E dispara um
// broadcast de "composing" junto. Consultar se um cliente está online faria o
// celular dele mostrar "digitando..." da nossa parte, sem ninguém digitando.
// Repetido pela lista inteira, é exatamente o padrão automatizado que causou o
// ban de 24h em 2026-05-27. Ver README, seção "Indicador de atividade".
//
// Vantagem desta abordagem: vale pra 100% dos contatos, inclusive quem desligou
// "visto por último" nas configurações de privacidade — porque o dado é nosso.

const MIN = 60000;

/**
 * Minutos desde a última mensagem do contato. null se ele nunca escreveu
 * (conversa aberta pelo atendente, por exemplo).
 */
export function minutesSinceContactWrote(lastClientMessageAt) {
  if (!lastClientMessageAt) return null;
  const t = new Date(lastClientMessageAt).getTime();
  if (Number.isNaN(t)) return null;
  return Math.floor((Date.now() - t) / MIN);
}

/**
 * Faixa de atividade, usada pra cor do ponto:
 *   'recent' — escreveu há menos de 5min; quase certamente está no WhatsApp agora
 *   'active' — escreveu na última hora
 *   'today'  — escreveu nas últimas 24h
 *   null     — mais antigo que isso, ou nunca escreveu → não mostra ponto nenhum
 *
 * O corte em 24h é proposital: um ponto em toda conversa vira ruído visual e
 * para de comunicar qualquer coisa.
 */
export function activityLevel(lastClientMessageAt) {
  const mins = minutesSinceContactWrote(lastClientMessageAt);
  if (mins === null || mins < 0) return null;
  if (mins < 5) return 'recent';
  if (mins < 60) return 'active';
  if (mins < 60 * 24) return 'today';
  return null;
}

/** Duração curta pro ponto da lista: "agora", "12min", "3h". */
export function activityAge(lastClientMessageAt) {
  const mins = minutesSinceContactWrote(lastClientMessageAt);
  if (mins === null) return null;
  if (mins < 1) return 'agora';
  if (mins < 60) return `${mins}min`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h`;
  return `${Math.floor(hrs / 24)}d`;
}

/**
 * Texto do tooltip / cabeçalho. Sempre deixa explícito que a referência é a
 * última mensagem DELE — um rótulo ambíguo aqui vira "o sistema disse que
 * estava online" na boca da equipe.
 */
export function activityLabel(lastClientMessageAt) {
  const mins = minutesSinceContactWrote(lastClientMessageAt);
  if (mins === null) return 'O contato ainda não enviou mensagens';
  if (mins < 1) return 'Escreveu agora mesmo';
  if (mins < 5) return `Escreveu há ${mins}min — provavelmente está no WhatsApp`;
  if (mins < 60) return `Escreveu há ${mins}min`;

  const d = new Date(lastClientMessageAt);
  const hoje = new Date();
  const mesmoDia = d.toDateString() === hoje.toDateString();
  const hora = d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });

  if (mesmoDia) return `Última mensagem dele hoje às ${hora}`;

  const ontem = new Date(hoje);
  ontem.setDate(hoje.getDate() - 1);
  if (d.toDateString() === ontem.toDateString()) return `Última mensagem dele ontem às ${hora}`;

  return `Última mensagem dele em ${d.toLocaleDateString('pt-BR')} às ${hora}`;
}

/** Versão curta pro cabeçalho do chat aberto. */
export function activityHeadline(lastClientMessageAt) {
  const mins = minutesSinceContactWrote(lastClientMessageAt);
  if (mins === null) return null;
  if (mins < 1) return 'ativo agora';
  if (mins < 60) return `ativo há ${mins}min`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `ativo há ${hrs}h`;

  const d = new Date(lastClientMessageAt);
  const hora = d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
  const ontem = new Date();
  ontem.setDate(ontem.getDate() - 1);
  if (d.toDateString() === ontem.toDateString()) return `visto ontem às ${hora}`;
  return `visto em ${d.toLocaleDateString('pt-BR')}`;
}
